import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useDebouncing from '../components/useDebouncing';
import {
  useGetClinicianSpecificToFacilityIdList,
  useGetFacilitiesSpecificToClinicianIdList,
} from '../../API/hooks/ClinicianHook';
import { rowsPerPage } from '../constants';
import ClinicianTable from '../clinicians/clinicianTable';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import {
  FACILITY_CLINICAIN_MAPPING_VIEW_ROUTE_PATH,
  NEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH,
  NEW_FACILITY_CLINICIAN_MAPPED_ROUTE_PATH,
  VIEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH,
} from '../../routers/routePaths';

import FacilityTable from '../facilities/facilityTable';
import { hasRole } from '../login/storageUtility';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';

type facilityDeatils = {
  facilityName?: string;
  providerName?: string;
  type: string;
  ids?: number;
};
const FacilityClinicianMapingList = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';

  const { facilityId, clinicianId } = useParams<{
    facilityId?: string;
    clinicianId?: string;
  }>();

  const location = useLocation();
  const facilityDetails = location.state as facilityDeatils | null;

  console.log('Details', facilityDetails);

  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');

  const { facilityName, providerName, type } = facilityDetails || {};

  const idRef = useRef<facilityDeatils | null>(null);

  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );

  const [searchTermFacilityMappedList, setSearchTermFacilityMappedList] =
    useState<string>('');
  const debouncedSearchTerm = useDebouncing(searchTermFacilityMappedList, 500);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const {
    ClinicianSpecificToFacilityListResponse,
    isLoading: isLoadingClinicians,
    refetch: refetchClinicians,
  } = useGetClinicianSpecificToFacilityIdList({
    userId: userId,
    facilityId: Number(facilityId),
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
  });

  const {
    FacilitiesSpecificToClinicianListResponse,
    isLoading: isLoadingFacilitiesList,
    refetch: refetchFacilitiesList,
  } = useGetFacilitiesSpecificToClinicianIdList({
    userId: userId,
    clinicianId: Number(clinicianId),
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
  });

  const ClinicianListCount =
    ClinicianSpecificToFacilityListResponse?.totalRecords ?? 0;

  const FacilityListCount =
    FacilitiesSpecificToClinicianListResponse?.totalRecords ?? 0;

  useEffect(() => {
    if (facilityDetails) {
      idRef.current = facilityDetails;
    }
  }, [facilityDetails]);

  useEffect(() => {
    if (!facilityId || !clinicianId || !location.state?.type) return;

    if (type === 'facility') {
      refetchClinicians();
    }

    if (type === 'clinician') {
      refetchFacilitiesList();
    }
  }, [location.state, page]);

  useEffect(() => {
    if (location?.state?.message) {
      setMessage(location?.state?.message);
      setOpenToast(true);
    }
  }, [location.state]);

  const handleFacilityMappedListSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;

    // Remove leading spaces
    value = value.replace(/^\s+/, '');

    // Replace multiple spaces with a single space
    value = value.replaceAll(/\s{2,}/g, ' ');

    setSearchTermFacilityMappedList(value);
    setPage(0);
  };

  const handleFacilityClinicianMappingEdit = (facilityId: number) => {
    type === 'facility'
      ? navigate(
          NEW_FACILITY_CLINICIAN_MAPPED_ROUTE_PATH + `/${idRef?.current?.ids}`,
          {
            state: {
              facilityName: facilityName,
              action: 'edit',
              type: type,
              ids: facilityId,
            },
            replace: true,
          }
        )
      : navigate(
          NEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH + `/${clinicianId}`,
          {
            state: {
              facilityName: providerName,
              action: 'edit',
              type: type,
              ids: facilityId,
            },
            replace: true,
          }
        );
  };

  const handleFacilityClinicianMappingView = (id: number) => {
    type === 'facility'
      ? navigate(
          FACILITY_CLINICAIN_MAPPING_VIEW_ROUTE_PATH +
            `/${idRef?.current?.ids}`,
          {
            state: {
              facilityName: facilityName,
              action: 'view',
              type: type,
              ids: id,
            },
          }
        )
      : navigate(
          VIEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH + `/${clinicianId}`,
          {
            state: {
              facilityName: providerName,
              action: 'view',
              type: type,
              ids: id,
            },
          }
        );
  };

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!toast) return;
    if (toast === 'success') {
      setMessage('Mapping Saved Successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Mapping updated successfully!');
      setOpenToast(true);
    }
  }, [toast]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="toast-facility-clinician-maping-list"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title-facility-clinician-maping-list"
              variant="h4"
              className="text-align-left"
            >
              {type === 'facility'
                ? `${facilityName} > Clinicians`
                : `${providerName} > Facilities`}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-search-facility-clinician-maping-list"
                  name="search-facility-clinician-maping-list"
                  placeholder="Search Clinicians"
                  size="small"
                  type="text"
                  value={searchTermFacilityMappedList}
                  onChange={handleFacilityMappedListSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-clinician-to-facility"
                    color="primary"
                    size="large"
                    onClick={() => {
                      type === 'facility'
                        ? navigate(
                            NEW_FACILITY_CLINICIAN_MAPPED_ROUTE_PATH +
                              `/${facilityId}`,
                            {
                              state: {
                                facilityName: facilityName,
                                action: 'save',
                                type: type,
                                ids: idRef.current?.ids,
                              },
                              replace: true,
                            }
                          )
                        : navigate(
                            NEW_CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH +
                              `/${clinicianId}`,
                            {
                              state: {
                                facilityName: providerName,
                                action: 'save',
                                type: type,
                                ids: idRef.current?.ids,
                              },
                              replace: true,
                            }
                          );
                    }}
                  >
                    Add Clinician to Facility
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        {type === 'facility' && (
          <ClinicianTable
            isLoading={isLoadingClinicians}
            clinicians={
              ClinicianSpecificToFacilityListResponse?.facilityToClinicianMapsList ??
              []
            }
            totalRecords={ClinicianListCount}
            page={page}
            rowsPerPage={rowsPerPage}
            sortField={sortField ?? ''}
            sortType={sortType ?? 'desc'}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onEdit={handleFacilityClinicianMappingEdit}
            onView={handleFacilityClinicianMappingView}
            getSortIcon={getSortIcon}
            getStartItem={getStartItem}
            getEndItem={getEndItem}
            action="mapping"
          />
        )}
        {type === 'clinician' && (
          <FacilityTable
            isLoading={isLoadingFacilitiesList}
            facility={
              FacilitiesSpecificToClinicianListResponse?.clinicianToFacilityMapsList ??
              []
            }
            totalRecords={FacilityListCount}
            page={page}
            rowsPerPage={rowsPerPage}
            sortField={sortField ?? ''}
            sortType={sortType ?? 'desc'}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onEdit={handleFacilityClinicianMappingEdit}
            onView={handleFacilityClinicianMappingView}
            getSortIcon={getSortIcon}
            getStartItem={getStartItem}
            getEndItem={getEndItem}
            action="mapping"
          />
        )}
      </Box>
    </Box>
  );
};

export default FacilityClinicianMapingList;
